/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Box,
  Checkbox,
  Autocomplete,
  Typography,
} from '@mui/material';
import { Iconify } from 'src/components';
import { APPLE, appleInputSx } from './appleDesignTokens';

// Normalise a filter's options to {value, label} — callers may pass plain
// strings (legacy) or objects. Object form is required whenever display
// labels can collide across distinct values (e.g. two practices named alike).
const norm = (opts) => (opts || []).map((o) => (
  typeof o === 'object' && o !== null ? o : { value: o, label: o }
));

// Shared Autocomplete chrome — matches the previous Select pill (gray fill,
// rounded, transparent border that reveals on hover/focus).
const acSx = (hasValue) => ({
  minWidth: hasValue ? 168 : 140,
  '& .MuiOutlinedInput-root': {
    borderRadius: APPLE.inputRadius,
    bgcolor: APPLE.grayBg,
    fontSize: '0.8rem',
    fontFamily: APPLE.font,
    fontWeight: 500,
    py: '2px',
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: APPLE.grayBorder },
    '&.Mui-focused fieldset': { borderColor: APPLE.accent, borderWidth: '2px' },
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiAutocomplete-input': { fontSize: '0.8rem', fontFamily: APPLE.font, py: '4px !important' },
});

const menuSlotProps = {
  paper: { sx: { borderRadius: '12px', mt: 0.5, fontFamily: APPLE.font } },
};

const alphaAccent = `${APPLE.accent}1A`; // ~10% accent

// Single-select with type-ahead search.
const SingleFilter = ({ f }) => {
  const options = norm(f.options);
  const selected = options.find((o) => String(o.value) === String(f.value)) || null;
  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      getOptionLabel={(o) => o.label || ''}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v.value)}
      onChange={(e, nv) => f.onChange(nv ? nv.value : '')}
      slotProps={menuSlotProps}
      sx={acSx(Boolean(f.value))}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={f.label}
          inputProps={{ ...params.inputProps, 'data-testid': `filter-${f.label}` }}
        />
      )}
    />
  );
};

SingleFilter.propTypes = { f: PropTypes.object.isRequired };

// Multi-select with type-ahead search + checkboxes. Tags collapse to a single
// "Label (N)" pill so the control never grows with the selection count.
const MultiFilter = ({ f }) => {
  const options = norm(f.options);
  const value = options.filter((o) => (f.value || []).map(String).includes(String(o.value)));
  const count = value.length;
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      options={options}
      value={value}
      getOptionLabel={(o) => o.label || ''}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v.value)}
      onChange={(e, nv) => f.onChange(nv.map((o) => o.value))}
      slotProps={menuSlotProps}
      sx={acSx(count > 0)}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={String(option.value)}>
          <Checkbox
            size="small"
            checked={selected}
            sx={{ p: 0.25, mr: 0.5, color: '#C4CDD5', '&.Mui-checked': { color: '#00AB55' } }}
          />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: APPLE.font }}>{option.label}</Typography>
        </li>
      )}
      // Collapse all chips into one compact count pill on the first tag slot;
      // render nothing for the rest so the input stays a fixed size.
      renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
        index === 0 ? (
          <Chip
            {...getTagProps({ index })}
            key="count"
            size="small"
            label={`${f.label} (${count})`}
            onDelete={undefined}
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 600,
              fontFamily: APPLE.font,
              bgcolor: alphaAccent,
              color: APPLE.accent,
              border: 'none',
              ml: 0.25,
            }}
          />
        ) : null
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={count === 0 ? f.label : ''}
          inputProps={{ ...params.inputProps, 'data-testid': `filter-${f.label}` }}
        />
      )}
    />
  );
};

MultiFilter.propTypes = { f: PropTypes.object.isRequired };

const TemplateFilterToolbar = ({
  search,
  onSearchChange,
  filters,
  multiFilters = [],
  selectedCount = 0,
  bulkActions = [],
  extraActions = null,
  toggles = [],
}) => (
  <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
      <TextField
        size="small"
        placeholder="Search templates..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 200, flex: 1, fontFamily: APPLE.font, ...appleInputSx }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: APPLE.grayText, width: 20, height: 20 }} />
            </InputAdornment>
          ),
        }}
      />
      {filters.map((f) => <SingleFilter key={f.label} f={f} />)}
      {multiFilters && multiFilters.map((f) => <MultiFilter key={f.label} f={f} />)}
      {toggles && toggles.map((t) => (
        <Button
          key={t.label}
          size="small"
          onClick={() => t.onChange(!t.value)}
          data-testid={t.testId || `toggle-${t.label}`}
          startIcon={<Iconify icon={t.value ? (t.iconOn || 'eva:eye-off-2-outline') : (t.iconOff || 'eva:eye-outline')} width={16} />}
          sx={{
            fontFamily: APPLE.font,
            fontSize: '0.78rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: APPLE.inputRadius,
            px: 1.25,
            py: 0.5,
            whiteSpace: 'nowrap',
            color: t.value ? APPLE.accent : APPLE.grayText,
            bgcolor: t.value ? alphaAccent : 'transparent',
            border: `1px solid ${t.value ? `${APPLE.accent}4D` : APPLE.grayBorder}`,
            '&:hover': { bgcolor: t.value ? `${APPLE.accent}26` : APPLE.grayBg },
          }}
        >
          {t.label}
        </Button>
      ))}
      <Box sx={{ flex: '0 0 auto' }}>
        {extraActions || null}
      </Box>
    </Stack>

    {selectedCount > 0 && bulkActions?.length > 0 && (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{
        mt: 2,
        py: 1.25,
        px: 2,
        bgcolor: APPLE.accentLight,
        borderRadius: APPLE.cardRadius,
        border: `1px solid ${APPLE.accent}20`,
      }}>
        <Chip size="small" label={`${selectedCount} selected`} color="primary" sx={{ fontWeight: 600, fontFamily: APPLE.font }} />
        {bulkActions.map((action) => (
          <React.Fragment key={action.label}>
            {action.selectComponent || null}
            <Button
              size="small"
              variant="contained"
              onClick={action.onClick}
              disabled={action.disabled}
              startIcon={action.icon ? <Iconify icon={action.icon} width={16} /> : null}
              sx={{ borderRadius: APPLE.inputRadius, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', fontFamily: APPLE.font }}
            >
              {action.loading ? 'Applying...' : action.label}
            </Button>
          </React.Fragment>
        ))}
      </Stack>
    )}
  </Box>
);

const filterOptionShape = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  }),
]);

TemplateFilterToolbar.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  // Single-select filters — rendered as searchable Autocompletes. value is a
  // string; onChange receives the chosen value (or '' when cleared).
  filters: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(filterOptionShape).isRequired,
  })).isRequired,
  // Multi-select filters — rendered as searchable multi Autocompletes with
  // checkboxes. value is an array; onChange receives an array.
  multiFilters: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(filterOptionShape).isRequired,
  })),
  // Boolean toggle pills (e.g. "Hide Unlayer emails"). value is a bool;
  // onChange receives the next bool.
  toggles: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    testId: PropTypes.string,
    iconOn: PropTypes.string,
    iconOff: PropTypes.string,
  })),
  selectedCount: PropTypes.number,
  bulkActions: PropTypes.array,
  extraActions: PropTypes.node,
};

export default TemplateFilterToolbar;
